import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { cartUtils, inr } from "@/lib/api";
import { Minus, Plus, Trash2 } from "lucide-react";

export default function CartPage() {
  const [items, setItems] = useState([]);
  const nav = useNavigate();

  useEffect(() => {
    const load = () => setItems(cartUtils.get());
    load();
    window.addEventListener("cart-updated", load);
    return () => window.removeEventListener("cart-updated", load);
  }, []);

  const total = items.reduce((s, i) => s + i.price * i.quantity, 0);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-32 text-center">
        <p className="eyebrow">Your bag is quiet</p>
        <h1 className="font-serif text-5xl mt-2">Nothing here yet.</h1>
        <p className="mt-4 text-stone-600">Wander through the collection — find a piece that speaks to your home.</p>
        <Link to="/shop/sofa" data-testid="cart-empty-shop-link" className="btn-primary inline-block mt-8">Shop the Collection</Link>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-6 md:px-12 py-12">
      <h1 className="font-serif text-4xl md:text-5xl tracking-tight">Your Bag</h1>
      <div className="mt-10 grid md:grid-cols-3 gap-12">
        <div className="md:col-span-2 divide-y divide-stone-200 border-t border-b border-stone-200">
          {items.map((i) => (
            <div key={i.product_id} data-testid={`cart-item-${i.product_id}`} className="flex gap-4 py-6">
              <img src={i.image} alt={i.name} className="w-24 h-28 object-cover bg-stone-100"/>
              <div className="flex-1">
                <h3 className="font-serif text-xl">{i.name}</h3>
                <p className="text-sm text-stone-500 mt-1">{inr(i.price)}</p>
                <div className="mt-3 flex items-center gap-4">
                  <div className="flex items-center border border-stone-300">
                    <button data-testid={`cart-minus-${i.product_id}`} onClick={() => cartUtils.updateQty(i.product_id, i.quantity - 1)} className="p-2"><Minus size={12}/></button>
                    <span className="w-8 text-center text-sm">{i.quantity}</span>
                    <button data-testid={`cart-plus-${i.product_id}`} onClick={() => cartUtils.updateQty(i.product_id, i.quantity + 1)} className="p-2"><Plus size={12}/></button>
                  </div>
                  <button data-testid={`cart-remove-${i.product_id}`} onClick={() => cartUtils.remove(i.product_id)} className="text-stone-500 hover:text-red-700"><Trash2 size={16}/></button>
                </div>
              </div>
              <p className="text-sm">{inr(i.price * i.quantity)}</p>
            </div>
          ))}
        </div>
        <div>
          <div className="bg-white border border-stone-200 p-6">
            <p className="eyebrow">Summary</p>
            <div className="mt-4 flex justify-between text-sm"><span>Subtotal</span><span data-testid="cart-subtotal">{inr(total)}</span></div>
            <div className="mt-2 flex justify-between text-sm text-stone-500"><span>Shipping</span><span>Calculated at checkout</span></div>
            <div className="mt-4 pt-4 border-t border-stone-200 flex justify-between text-lg font-serif"><span>Total</span><span data-testid="cart-total">{inr(total)}</span></div>
            <button data-testid="cart-checkout-btn" onClick={() => nav("/checkout")} className="btn-primary w-full mt-6">Proceed to Checkout</button>
            <Link to="/shop/sofa" className="mt-4 block text-center text-xs tracking-widest uppercase link-underline">Continue Shopping</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
